'use client';

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { ArrowUp, FileText, Film, Loader2, Plus, X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { composeAgentExtras, modelSupportsVision } from '@/lib/editor/agent/compose';
import { detectMentionQuery, stripMentionFragment, type MentionQuery } from '@/lib/editor/agent/mentions';
import { useEditorStore, type EditorMedia } from '@/lib/editor/editorStore';
import type { AgentExtras, ChatAttachment, MentionRef } from '@/types/editor';
import { useAttachmentUpload } from './hooks/useAttachmentUpload';
import { MentionPopup } from './MentionPopup';

const MAX_ROWS = 5;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
// Doc extraction (Canny task 2C): 15MB matches the `/api/editor-agent/extract`
// route's own cap. Extension-based (not MIME-based) because browsers report
// wildly inconsistent `File.type` for `.docx`/`.txt` — the route itself also
// branches on filename suffix. `.doc` (legacy binary Word) is deliberately
// NOT accepted: the extract route's `mammoth.extractRawText` only actually
// parses the `.docx` zip/XML format, not the old binary one.
const ACCEPTED_DOC_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const MAX_DOC_BYTES = 15 * 1024 * 1024;
const FILE_INPUT_ACCEPT = 'image/png,image/jpeg,image/webp,application/pdf,.pdf,.docx,.txt';

function isDocFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return ACCEPTED_DOC_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

interface ChatComposerProps {
  onSend: (text: string, extras: AgentExtras) => void;
  disabled: boolean;
  busy: boolean;
}

/**
 * Chat input for the editor sidebar (Canny task 4): auto-growing textarea +
 * removable mention chips + the `@`-mention library popup.
 *
 * Keyboard-collision note: `MentionPopup` owns Enter/↑/↓/Esc via its own
 * `window` keydown listener while mounted (see `MentionPopup.tsx`). Native
 * keydown events bubble textarea → React root → document → window, so this
 * component's `onKeyDown` always runs BEFORE that window listener for the
 * same keypress. We exploit that ordering: whenever a mention query is
 * active (i.e. the popup is mounted), `handleKeyDown` returns immediately
 * without calling `preventDefault`/`stopPropagation`, letting the event
 * continue bubbling up to the popup's window listener, which alone decides
 * what Enter does (pick the active row, or no-op if the filtered list is
 * empty). Only when no mention query is active — meaning the popup isn't
 * mounted and has no listener to hand off to — does this handler treat
 * Enter (no Shift) as "send".
 */
export function ChatComposer({ onSend, disabled, busy }: ChatComposerProps) {
  const { t } = useI18n();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, extractDoc } = useAttachmentUpload();
  const modelId = useEditorStore((s) => s.modelId);

  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<MentionRef[]>([]);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
  const attachmentCounterRef = useRef(0);

  // Auto-grow the textarea between 1 and MAX_ROWS lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight || '20') || 20;
    const maxHeight = lineHeight * MAX_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [text]);

  const syncMentionQuery = (value: string, caret: number) => {
    setMentionQuery(detectMentionQuery(value, caret));
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;
    setText(el.value);
    syncMentionQuery(el.value, el.selectionStart ?? el.value.length);
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    syncMentionQuery(el.value, el.selectionStart ?? el.value.length);
  };

  const handleClick = (e: ReactMouseEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    syncMentionQuery(el.value, el.selectionStart ?? el.value.length);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery) {
      // Popup is mounted and owns Enter/↑/↓/Esc via its window listener —
      // don't intercept, just let the keydown bubble up to it.
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePick = (media: EditorMedia) => {
    setMentions((prev) => (prev.some((m) => m.id === media.id) ? prev : [...prev, { id: media.id, title: media.title }]));
    setText((prev) => (mentionQuery ? stripMentionFragment(prev, mentionQuery) : prev));
    const caretPos = mentionQuery?.start;
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      if (caretPos != null) el.setSelectionRange(caretPos, caretPos);
    });
  };

  const handleClosePopup = () => setMentionQuery(null);

  const handleRemoveMention = (id: string) => {
    setMentions((prev) => prev.filter((m) => m.id !== id));
  };

  const hasImages = attachments.some((a) => a.kind === 'image');
  const visionOk = modelSupportsVision(modelId);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file later
    for (const file of files) {
      if (ACCEPTED_IMAGE_TYPES.has(file.type)) {
        if (file.size > MAX_IMAGE_BYTES) {
          console.warn('[ChatComposer] skipping attachment over 8MB', file.name);
          continue;
        }
        const localId = `img-${attachmentCounterRef.current++}-${file.name}`;
        const previewUrl = URL.createObjectURL(file);
        setAttachments((prev) => [...prev, { localId, kind: 'image', name: file.name, status: 'uploading', previewUrl }]);

        uploadImage(file)
          .then((url) => {
            setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, status: 'ready', url } : a)));
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Upload failed';
            setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, status: 'error', error: message } : a)));
          });
        continue;
      }

      if (isDocFile(file)) {
        if (file.size > MAX_DOC_BYTES) {
          console.warn('[ChatComposer] skipping attachment over 15MB', file.name);
          continue;
        }
        // Docs don't need a preview thumbnail / objectURL — no `previewUrl`.
        const localId = `doc-${attachmentCounterRef.current++}-${file.name}`;
        setAttachments((prev) => [...prev, { localId, kind: 'doc', name: file.name, status: 'uploading' }]);

        extractDoc(file)
          .then((text) => {
            setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, status: 'ready', text } : a)));
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Extraction failed';
            setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, status: 'error', error: message } : a)));
          });
        continue;
      }

      console.warn('[ChatComposer] skipping attachment with unsupported type', file.type || file.name);
    }
  };

  const handleRemoveAttachment = (localId: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  };

  const canSend =
    (text.trim().length > 0 || mentions.length > 0 || attachments.some((a) => a.status === 'ready')) &&
    !disabled &&
    !busy &&
    !attachments.some((a) => a.status === 'uploading') &&
    !(hasImages && !visionOk);

  const handleSend = () => {
    if (!canSend) return;
    const extras = composeAgentExtras(mentions, attachments);
    onSend(text.trim(), extras);
    setText('');
    setMentions([]);
    setMentionQuery(null);
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
  };

  return (
    <div className="relative px-4 pb-4 pt-2" aria-busy={busy}>
      {mentionQuery && (
        <MentionPopup query={mentionQuery.query} onPick={handlePick} onClose={handleClosePopup} />
      )}

      {(mentions.length > 0 || attachments.length > 0) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {mentions.map((m) => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white/80"
            >
              <Film className="h-3 w-3 shrink-0 text-white/50" strokeWidth={2} />
              <span className="max-w-[120px] truncate">{m.title}</span>
              <button
                type="button"
                onClick={() => handleRemoveMention(m.id)}
                className="ml-0.5 shrink-0 text-white/40 transition hover:text-white/80"
                aria-label={`Убрать ${m.title}`}
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </span>
          ))}

          {attachments.map((a) => (
            <span
              key={a.localId}
              className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-lg border px-1.5 py-1 text-[11px] ${
                a.status === 'error'
                  ? 'border-red-500/40 bg-red-500/10 text-red-200'
                  : 'border-white/10 bg-white/5 text-white/80'
              }`}
              title={a.error ?? a.name}
            >
              {a.kind === 'image' && a.previewUrl && (
                <span className="relative h-5 w-5 shrink-0 overflow-hidden rounded">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={a.previewUrl} alt={a.name} className="h-full w-full object-cover" />
                  {a.status === 'uploading' && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-3 w-3 animate-spin text-white" strokeWidth={2.5} />
                    </span>
                  )}
                </span>
              )}
              {a.kind === 'doc' && <FileText className="h-3.5 w-3.5 shrink-0 text-white/50" strokeWidth={2} />}
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="max-w-[100px] truncate">{a.name}</span>
                {a.kind === 'doc' && <span className="text-[9px] text-white/40">сценарий</span>}
              </span>
              {a.kind === 'doc' && a.status === 'uploading' && (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-white/60" strokeWidth={2.5} />
              )}
              <button
                type="button"
                onClick={() => handleRemoveAttachment(a.localId)}
                className="ml-0.5 shrink-0 text-white/40 transition hover:text-white/80"
                aria-label={`Убрать ${a.name}`}
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}

      {hasImages && !visionOk && (
        <div className="mb-2 text-[11px] text-amber-300/90">
          Выбранная модель не видит изображения — выберите vision-модель
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        multiple
        onChange={handleFilesSelected}
        className="hidden"
      />

      <div className="flex items-end gap-1.5 rounded-xl border border-white/10 bg-white/5 py-1.5 pl-1.5 pr-1.5">
        <button
          type="button"
          onClick={handleAttachClick}
          title="Прикрепить изображение или документ"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white/85"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
        </button>

        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          placeholder={t('studio.editor.chat.placeholder')}
          className="min-w-0 flex-1 resize-none bg-transparent px-1 py-1 text-sm leading-relaxed text-white placeholder:text-white/40 outline-none"
        />

        <button
          type="button"
          disabled={!canSend}
          onClick={handleSend}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-brand-600/55"
          aria-label={busy ? 'Canny обрабатывает запрос' : t('studio.editor.chat.send')}
          aria-busy={busy}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <ArrowUp className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
